import React, { useState, useContext } from 'react';
import classnames from 'classnames';
import Styled from 'styled';
import { Link } from 'react-router-dom';
import { FormattedMessage, FormattedTime, FormattedDate } from 'react-intl';
import { useMutation } from '@apollo/client/react/hooks';

import SVGIcon from 'lib/images/svg-icon/svg-icon';

import { ViewerContext } from 'four-nets/user/viewer/viewer';
import Avatar from 'four-nets/avatar/avatar';
import TextArea from 'four-nets/ui-v2/form/text-area/text-area';
import replyIcon from 'four-nets/icons/svg/icon_reply.svg';

import mutation, {
    UpdateUserBrandReplyMutation,
    UpdateUserBrandReplyMutationVariables,
} from './mutations/update-user-brand-reply.graphql';
import { VendorUserBrandFragment } from './vendor-user-brands.graphql';
import defaultStyles from './vendor-user-brand.scss';

export interface Props extends VendorUserBrandFragment {
    styles: typeof defaultStyles;
    username: string;
    refetch: () => any;
    isReviewedVendor: boolean;
}

interface ItemProps {
    styles: typeof defaultStyles;

    user: VendorUserBrandFragment['user'];
    isReply?: boolean;
    reply?: any;

    username: string;
    description: string;
    createdTime: Date | null;

    setOpen?: (value: boolean) => any;
    open?: boolean;
}

const Item: React.FC<ItemProps> = props => {
    const { styles, user, username, createdTime, description, isReply, reply, setOpen, open } = props;

    return (
        <div className={classnames(styles.item, { [styles.reply]: isReply })}>
            {!isReply && !reply && setOpen && open !== undefined ? (
                <button
                    className={styles.replyButton}
                    onClick={() => {
                        setOpen(!open);
                    }}
                >
                    <SVGIcon style={{ image: styles.icon }} src={replyIcon} />
                    <FormattedMessage id='REPLY' values={{ gender: 'v' }} />
                </button>
            ) : null}
            <div className={styles.itemContent}>
                <div className={styles.itemContent2}>
                    <div className={styles.avatarWrapper}>
                        <Avatar user={user} />
                    </div>
                    <div className={styles.flexBlock}>
                        {user ? (
                            <div className={classnames(styles.user, { [styles.spaceEvenly]: Boolean(createdTime) })}>
                                <Link to={user.href} className={styles.username}>
                                    {username}
                                </Link>
                                {createdTime ? (
                                    <span className={styles.date}>
                                        <FormattedDate value={createdTime} />
                                    </span>
                                ) : null}
                            </div>
                        ) : null}
                        <div className={classnames(styles.description, styles.desktop)}>
                            {description || <FormattedMessage id='RECOMMENDS' />}
                        </div>
                    </div>
                </div>
                <div className={classnames(styles.description, styles.mobile)}>
                    {description || <FormattedMessage id='RECOMMENDS' />}
                </div>
            </div>
        </div>
    );
};

const VendorUserBrand: React.FC<Props> = React.memo(props => {
    const { styles, isReviewedVendor, refetch, username, ...userBrand } = props;
    const { id, user, reply: replyProps, createdTime } = userBrand;

    const [open, setOpen] = useState(false);
    const [reply, setReply] = useState(replyProps || '');

    const [replyFn] = useMutation<UpdateUserBrandReplyMutation, UpdateUserBrandReplyMutationVariables>(mutation);

    const { viewer } = useContext(ViewerContext);

    const canReply = viewer && viewer.username === username;

    return (
        <>
            <Item
                {...props}
                username={user.username}
                createdTime={new Date(createdTime)}
                open={canReply ? open : undefined}
                setOpen={canReply ? setOpen : undefined}
                reply={replyProps}
            />
            {replyProps && !open ? (
                <Item
                    styles={styles}
                    isReply={true}
                    description={replyProps}
                    createdTime={null}
                    user={userBrand.brand! && userBrand.brand.vendor! && userBrand.brand.vendor!.user!}
                    username={username}
                />
            ) : null}
            {open ? (
                <div className={classnames(styles.item, styles.reply)}>
                    <div className={styles.avatarWrapper}>
                        <Avatar user={viewer} />
                    </div>
                    <div className={styles.column}>
                        <div className={styles.user}>
                            <span className={styles.username}>{username}</span>
                            {createdTime ? (
                                <span className={styles.date}>
                                    <FormattedTime value={new Date()} />
                                </span>
                            ) : null}
                        </div>
                        <TextArea
                            style={{ textArea: styles.textArea, textAreaWrapper: styles.textAreaWrapper }}
                            onChange={e => {
                                setReply(e.target.value);
                            }}
                            value={reply}
                        />
                        <button
                            onClick={() => {
                                replyFn({ variables: { input: { id, reply } } }).then(() => {
                                    setOpen(false);
                                });
                            }}
                            className={styles.saveButton}
                        >
                            <FormattedMessage id='BUTTON_LABEL_SAVE' />
                        </button>
                    </div>
                </div>
            ) : null}
        </>
    );
});

export default Styled(defaultStyles)(VendorUserBrand);
